import type { Agent } from "../types";

// One reusable agent presentation, used everywhere the hub shows an agent: the
// live presence popup (expanded), session cards, the session-thread member row,
// and the tasks board (compact). Two variants:
//   • compact  — an inline pill: status dot + name + model chip (+ optional
//                operator, + optional remove ×, + selectable/clickable states).
//   • expanded — a card: robot avatar + name + org + status, with operator and
//                model on their own sub-lines.
// Keeping this in one place means the new `model` field shows up consistently
// across all four surfaces.

// A small robot glyph shared by the agent boxes.
function RobotGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="8" width="16" height="12" rx="2" />
      <path d="M12 8V4M9 4h6" />
      <circle cx="9" cy="14" r="1" />
      <circle cx="15" cy="14" r="1" />
    </svg>
  );
}

// Shorten a model id for the chip (full value stays in the title tooltip):
// "claude-opus-4-8" → "opus-4-8", "anthropic.claude-sonnet-4-6" → "sonnet-4-6".
export function modelLabel(model?: string | null): string {
  if (!model) return "";
  return model.replace(/^anthropic\./, "").replace(/^claude-/, "");
}

export interface AgentBoxProps {
  agent: Agent;
  variant?: "compact" | "expanded";
  /** Highlight as selected (compact picker pills). */
  selected?: boolean;
  /** Show the operator account inline (defaults on for expanded, off for compact). */
  showAccount?: boolean;
  /** Show the green online/live status dot (default true). Off for member lists. */
  showStatus?: boolean;
  /** Makes a compact box a clickable button (e.g. toggle in the task picker). */
  onClick?: () => void;
  /** Renders a × button (e.g. remove an agent from a session thread). */
  onRemove?: () => void;
  /** Override the tooltip; defaults to "account · model · status". */
  title?: string;
}

export default function AgentBox({
  agent,
  variant = "compact",
  selected,
  showAccount,
  showStatus = true,
  onClick,
  onRemove,
  title,
}: AgentBoxProps) {
  const online = agent.status === "online";
  const model = modelLabel(agent.model);
  const hint = title ?? [agent.account, agent.model, agent.status].filter(Boolean).join(" · ");

  if (variant === "expanded") {
    return (
      <div className={`agentbox expanded${online ? " online" : ""}`} title={hint}>
        <div className="agentbox-ava"><RobotGlyph /></div>
        <div className="agentbox-main">
          <div className="agentbox-line1">
            <span className="agentbox-name">{agent.name}</span>
            {agent.org && <span className="agentbox-org">{agent.org}</span>}
            {(showStatus || onRemove) && (
              <span className="agentbox-line1-end">
                {showStatus && <span className={`agent-dot${online ? " online" : ""}`} />}
                {onRemove && (
                  <button
                    className="agentbox-x"
                    title="Remove from session"
                    onClick={(e) => { e.stopPropagation(); onRemove(); }}
                  >
                    ×
                  </button>
                )}
              </span>
            )}
          </div>
          <div className="agentbox-sub">
            <span className="agentbox-sub-label">operator</span> {agent.account || "—"}
          </div>
          <div className="agentbox-sub">
            <span className="agentbox-sub-label">model</span>{" "}
            {agent.model
              ? <span className="agentbox-model">{model}</span>
              : <span className="agentbox-muted">unknown</span>}
          </div>
        </div>
      </div>
    );
  }

  const withAccount = showAccount ?? false;
  const inner = (
    <>
      {showStatus && <span className={`agent-dot${online ? " online" : ""}`} />}
      <span className="agentbox-name">{agent.name}</span>
      {agent.model && <span className="agentbox-model" title={agent.model ?? undefined}>{model}</span>}
      {withAccount && agent.account && <span className="agentbox-account">{agent.account}</span>}
    </>
  );

  // Clickable picker pill (no remove button inside a button — surfaces never need both).
  if (onClick) {
    return (
      <button
        type="button"
        className={`agentbox compact clickable${online ? " online" : ""}${selected ? " selected" : ""}`}
        title={hint}
        onClick={onClick}
      >
        {inner}
      </button>
    );
  }

  return (
    <span className={`agentbox compact${online ? " online" : ""}${selected ? " selected" : ""}`} title={hint}>
      {inner}
      {onRemove && (
        <button
          className="agentbox-x"
          title="Remove from session"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
        >
          ×
        </button>
      )}
    </span>
  );
}
